import {getEmployeeBasicBreadcrumb} from '@/library/modules/employee/employee';
import {getRouter, importWrapper} from '@/library/utils';
import axios, {AxiosError, type AxiosResponse} from 'axios';
import {type LocationAsRelativeRaw} from 'vue-router';
import {errorHandler, sendPopupRequest, XHR_STATUS_DEFAULT} from '@/utils/portuna';
import dateHelper from '../../utils/portuna/dateHelper';
import Schedule, {type ScheduleChooseActionType} from '../form/schedule';
import Loader from '../intranet/loader';
import {Popup, popupDefaultCancelButton} from '../popup';
import TopMessageHandler from '../topMessageHandler';

const TaskAddBarPopUp = importWrapper(() => import('../../views/task/components/TaskAddBarPopUp.vue'));

export const states = {
    remaining: {
        state: 0,
        title: 'Offen',
        icon: 'check_box_outline_blank',
        color: '',
    },
    resolved: {
        state: 2,
        title: 'Erledigt',
        icon: 'check_box',
        color: '#3b8136',
    },
    duplicate: {
        state: 5,
        title: 'Duplikat',
        icon: 'content_copy',
        color: '#ffa500',
    },

    // only subtasks
    notNecessary: {
        state: 6,
        title: 'Nicht notwendig',
        icon: 'close',
        color: '',
    },
};

export const taskTypes = {
    mine: {
        type: 'mine',
        icon: 'assignment_ind',
        label: 'Meine Aufgaben',
        labelShort: 'Meine',
        to: {
            name: 'task-list-mine',
        },
    },
    all: {
        type: 'all',
        icon: 'assignment_turned_in',
        label: 'Alle Aufgaben',
        labelShort: 'Alle',
        to: {
            name: 'task-list-all',
        },
    },
    traced: {
        type: 'traced',
        icon: 'flag',
        label: 'Verfolgte Aufgaben',
        labelShort: 'Verfolgte',
        to: {
            name: 'task-list-traced',
        },
    },
};

export const employeeTaskTypes = {
    mine: {
        type: 'mine',
        icon: 'assignment_ind',
        label: 'Meine Personalaufgaben',
        labelShort: 'Meine',
        to: {
            name: 'employee-task-list-mine',
        },
    },
    all: {
        type: 'all',
        icon: 'assignment_turned_in',
        label: 'Alle Personalaufgaben',
        labelShort: 'Alle',
        to: {
            name: 'employee-task-list-all',
        },
    },
    traced: {
        type: 'traced',
        icon: 'flag',
        label: 'Verfolgte Personalaufgaben',
        labelShort: 'Verfolgte',
        to: {
            name: 'employee-task-list-traced',
        },
    },
};

export const statesResolved = [
    states.resolved.state,
    states.duplicate.state,
    states.notNecessary.state,
];

export const priority = {
    normal: {
        value: 1,
        title: 'Normal',
        icon: 'remove',
        color: '',
    },
    high: {
        value: 2,
        title: 'Wichtig',
        icon: 'priority_high',
        color: '#ac4f3d',
    },
};

export const taskBasicBreadcrumb: Breadcrumb = {
    label: taskTypes.mine.label,
    href: taskTypes.mine.to,
};

export const categoriesUsageSetting: { [key: string]: number } = {
    global: 1,
    personal: 2,
    globalAndPersonal: 3,
};

export function deleteTask(task: TaskEntry): Promise<AxiosResponse>
{
    return new Promise((resolve, reject) =>
    {
        recurrencePrompt(task, 'delete')
        .then(({
            serial,
            prompted,
        }) =>
        {
            let message = 'Sie sind im Begriff, die Aufgabe &quot<strong>'
                + task.title + '</strong>&quot am ' + dateHelper('d.m.Y', task.recurrence_start)
                + (task.has_subtasks ? ' samt ihrer Unteraufgaben' : '')
                + ' zu löschen. Möchten Sie fortfahren?';

            let deleteUrl = '/task/entry/delete/id/' + task.id + '/start/' + task.recurrence_start + '/serial/' + (serial ? 1 : 0);

            let handleError = (e: AxiosError | string) =>
            {
                reject(e);
                e !== 'popupClosed' && TopMessageHandler.error('Die Aufgabe konnte nicht gelöscht werden.');
            };

            if (prompted)
            {
                axios.post(deleteUrl).then((response) =>
                {
                    if (response.status === 200 && response.data.status === XHR_STATUS_DEFAULT)
                    {
                        TopMessageHandler.success(response.data.message || 'Die Aufgabe wurde gelöscht.');

                        resolve(response);
                    }
                    else
                    {
                        TopMessageHandler.error(response.data.message || 'Die Aufgabe konnte nicht gelöscht werden.');
                        reject(response);
                    }
                })
                .catch(handleError);
            }
            else
            {
                sendPopupRequest({
                    type: 'delete',
                    url: deleteUrl,
                    message: {
                        title: 'Aufgabe l&ouml;schen',
                        message,
                        loader: 'Aufgabe wird gelöscht...',
                    },
                    options: {
                        titleIcon: 'delete',
                        textIcon: 'delete_forever',
                        textIconClass: 'text-danger',
                        buttonOkay: {
                            title: 'Löschen',
                            icon: 'delete',
                            type: 'danger',
                        },
                    },
                })
                .then(resolve)
                .catch(handleError);
            }
        })
        .catch(reject);
    });
}

export function toggleResolveTask(task: TaskEntry): Promise<TaskEntry>
{
    return new Promise((resolve, reject) =>
    {
        let resolved = statesResolved.indexOf(task.state) !== -1;

        Loader.show(resolved ? 'Die Erledigung der Aufgabe wird zurückgezogen.' : 'Die Aufgabe wird erledigt.');

        axios.post('/task/entry/resolve-task', {
            id: task.id,
            start: task.recurrence_start,
            state: resolved ? states.remaining.state : states.resolved.state,
        }).then(response =>
        {
            const {
                      data,
                      message,
                      status,
                  } = response.data;

            if (status === XHR_STATUS_DEFAULT)
            {
                resolve(data.task);

                TopMessageHandler.success(message);
            }
            else
            {
                reject(response);
                errorHandler(response);
            }
        })
        .catch(e =>
        {
            reject(e);
            errorHandler(e);
        })
        .finally(Loader.hide);

        return true;
    });
}

export function toggleTraceTask(task: TaskEntry): Promise<TaskEntry>
{
    return new Promise((resolve, reject) =>
    {
        Loader.show(task.is_traced ? 'Die Verfolgung der Aufgabe wird entfernt.' : 'Die Aufgabe wird verfolgt.');

        let action = task.is_traced ? 'delete-trace' : 'add-trace';

        axios.post('/task/entry/' + action + '/id/' + task.id + '/start/' + task.recurrence_start).then(response =>
        {
            const {
                      data,
                      message,
                      status,
                  } = response.data;

            if (status === XHR_STATUS_DEFAULT)
            {
                resolve(data.task);

                TopMessageHandler.success(message);
            }
            else
            {
                reject(response);
                errorHandler(response);
            }
        })
        .catch(e =>
        {
            reject(e);
            errorHandler(e);
        })
        .finally(Loader.hide);
    });
}

export function takeOverTask(task: TaskEntry, authUserAssignmentId: number, ignoreAbsenceCheck: boolean = false): Promise<{ task: TaskEntry, dateTargetHistory: TaskDateTargetHistoryEntry[] }>
{
    return new Promise((resolve, reject) =>
    {
        recurrencePrompt(<TaskEntry>task, 'take')
        .then(({serial}) =>
        {
            absenceCheck(authUserAssignmentId, task!.recurrence_start, ignoreAbsenceCheck)
            .then(() =>
            {
                Loader.show('Die Aufgabe wird übernommen...');

                axios.post('/task/entry/take-over-task', {
                    id: task.id,
                    start: task?.recurrence_start,
                    serial,
                    owner: authUserAssignmentId,
                    entity_id: 0,
                }).then(response =>
                {
                    const {
                              data,
                              message,
                              status,
                          } = response.data;

                    if (status === XHR_STATUS_DEFAULT)
                    {
                        resolve(data);

                        TopMessageHandler.success(message);
                    }
                    else
                    {
                        reject(response);
                        errorHandler(response);
                    }
                })
                .catch(e =>
                {
                    reject(e);
                    errorHandler(e);
                })
                .finally(Loader.hide);
            })
            .catch(reject);
        }).catch(reject);
    });
}

export function recurrencePrompt(task: TaskEntry, action: ScheduleChooseActionType, ignore = false): Promise<{ serial: boolean, prompted: boolean }>
{
    if (!task.has_recurrence || ignore)
    {
        return Promise.resolve({
            serial: false,
            prompted: false,
        });
    }

    return new Promise((resolve, reject) =>
    {
        let date = dateHelper('d.m.Y', task.recurrence_start);

        Schedule.showChoosePopup(task.title + ' am ' + date, action, 'feminine', 'Aufgabe', 'Aufgabe')
        .then((serial: boolean) =>
        {
            resolve({
                serial: serial,
                prompted: true,
            });
        })
        .catch((reason: 'popupClosed') =>
        {
            reject(reason);
        });
    });
}

export function absenceCheck(employeeId: number, date: string, ignore = false): Promise<void>
{
    return new Promise((resolve, reject) =>
    {
        let data = {
            date,
            employee_id: employeeId,
        };

        if (!employeeId || !date || ignore)
        {
            return resolve();
        }

        axios.post('/task/entry/check-absences', data).then(response =>
        {
            if (response.data.status === XHR_STATUS_DEFAULT)
            {
                if (!response.data.data.absences)
                {
                    resolve();
                    return true;
                }

                let buttons: PopupButton[] = [];

                buttons.push(Object.assign({}, popupDefaultCancelButton, {
                    cmd(popup: Popup)
                    {
                        popup.close();
                        reject('popupClosed');
                    },
                }));

                if (response.data.data.valid)
                {
                    buttons.push({
                        title: 'Verstanden',
                        icon: 'check',
                        type: 'primary',
                        cmd: () =>
                        {
                            popup.close();
                            resolve();
                        },
                    });
                }

                let popup = new Popup({
                    title: 'Abwesenheitshinweis',
                    titleIcon: 'block',
                    text: response.data.data.absences,
                    textIcon: 'error',
                    textIconClass: 'text-grey',
                    size: 'small',
                    buttons,
                });

                popup.open();
            }
            else
            {
                reject(response);
            }

            return true;
        })
        .catch(reject);

        return true;
    });
}

export function taskAddBarPopUp(parentId = 0, title = '', employeeId = 0): Promise<TaskEntry>
{
    return new Promise((resolve, reject) =>
    {
        TaskAddBarPopUp().then(Component =>
        {
            let handled = false;
            let popup   = new Popup({
                title: title || 'Aufgabe hinzufügen',
                component: Component.default,
                componentProps: {
                    parent: parentId,
                    mode: 'add',
                    employeeId: employeeId,
                },
                events: {
                    add: (task: TaskEntry) =>
                    {
                        handled = true;
                        popup.close();
                        resolve(task);
                    },
                },
                onClose: () =>
                {
                    if (!handled)
                    {
                        reject();
                    }
                },
            });

            popup.open();
        });
    });
}

export function taskEditLink(to: LocationAsRelativeRaw, callbackReload?: CallableFunction): Promise<any>
{
    return getRouter().push(to);
}

export function getRefModuleType(resolveRef: () => any)
{
    let ref = resolveRef();

    let type = 'task';

    if (ref?.name)
    {
        let match = ref.name.toString().match(/employee-task-list-(\w+)/);

        if (match)
        {
            type = 'employee';
        }
    }

    return type;
}

export function getRefListType(resolveRef: () => any)
{
    let ref = resolveRef();

    let type = '';

    if (ref?.name)
    {
        let match = ref.name.toString().match(/task-list-(\w+)/);

        if (match)
        {
            type = match[1];
        }
    }

    return type;
}

export function getBasicBreadcrumb(task: Nullable<TaskEntry> = null, authUserAssignmentId: number, resolveRef: () => any, getCurrentRefParam: () => any | string): Breadcrumb[]
{
    let type        = task?.owner && task?.owner === authUserAssignmentId ? 'mine' : (task?.is_traced ? 'traced' : 'all');
    let refListType = getRefListType(resolveRef);
    let href;

    if (refListType)
    {
        type = refListType;
        href = getCurrentRefParam();
    }

    if (getRefModuleType(resolveRef) === 'employee')
    {
        return [
            getEmployeeBasicBreadcrumb(),
            {
                label: employeeTaskTypes[type].label,
                href: href || employeeTaskTypes[type].to,
            },
        ];
    }

    return [
        {
            label: taskTypes[type].label,
            href: href || taskTypes[type].to,
        },
    ];
}

export function getReturnToListRoute(resolveRef: () => Nullable<string>, getCurrentRefParam: () => any | string)
{
    if (resolveRef())
    {
        return getCurrentRefParam();
    }
    else
    {
        return {name: 'task-list-all'};
    }
}